import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { membersAPI, paymentsAPI, settingsAPI } from '../../utils/api';
import { formatDate, daysUntilExpiry, formatCurrency } from '../../utils/helpers';
import StatusBadge from '../../components/StatusBadge';
import MemberAvatar from '../../components/MemberAvatar';
import ConfirmModal from '../../components/ConfirmModal';
import Modal from '../../components/Modal';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

const PLAN_MONTHS = { Monthly: 1, Quarterly: 3, Yearly: 12 };

function DaysRemaining({ expiryDate, status }) {
  if (status === 'Frozen') return <span className="text-xs text-blue-500 font-medium">Frozen</span>;
  if (status === 'Expired') return <span className="text-xs text-red-500 font-medium">Expired</span>;

  const days = daysUntilExpiry(expiryDate);
  const color = days <= 3 ? 'text-red-600 font-bold' : days <= 7 ? 'text-yellow-600 font-semibold' : 'text-gray-500';
  return <span className={`text-xs ${color}`}>{days}d left</span>;
}

function QuickPayModal({ member, pricing, onClose, onSuccess }) {
  const [mode, setMode] = useState('Cash');
  const [discount, setDiscount] = useState('0');
  const [calcData, setCalcData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (member) {
      paymentsAPI.calculate(member._id).then(res => setCalcData(res.data)).catch(() => {});
    }
  }, [member]);

  const showDiscount = member?.plan !== 'Monthly';
  const disc = parseFloat(discount) || 0;
  const payable = calcData ? Math.max(0, calcData.total - disc) : 0;

  const handlePay = async () => {
    setLoading(true);
    try {
      const res = await paymentsAPI.quickPay({ memberId: member._id, paymentMode: mode, discount: disc });
      const expLabel = res.data.newExpiry ? ` • New expiry: ${formatDate(res.data.newExpiry)}` : '';
      toast.success(`✅ ${formatCurrency(res.data.amount)} collected from ${member.name}${expLabel}`, { duration: 4000 });
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={!!member} onClose={onClose} title="Mark as Paid" size="sm">
      {!calcData ? (
        <LoadingSpinner message="Calculating amount..." />
      ) : (
        <div className="space-y-4">
          {/* Member info */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <MemberAvatar photo={member.photo} name={member.name} size="md" />
            <div>
              <p className="font-semibold text-gray-800">{member.name}</p>
              <p className="text-xs text-gray-500">{member.memberId} • {member.plan}</p>
            </div>
          </div>

          {/* Breakdown */}
          <div className="rounded-xl overflow-hidden border border-gray-100">
            {calcData.isFirstPayment && (
              <div className="flex justify-between px-4 py-2.5 bg-gray-50 text-sm border-b border-gray-100">
                <span className="text-gray-600">Admission Fee <span className="text-xs text-gray-400">(1st time)</span></span>
                <span className="font-medium">{formatCurrency(calcData.admissionFee)}</span>
              </div>
            )}
            <div className="flex justify-between px-4 py-2.5 bg-gray-50 text-sm border-b border-gray-100">
              <span className="text-gray-600">
                {member.plan} Fee
                <span className="text-xs text-gray-400 ml-1">
                  ({calcData.months} mo × {formatCurrency(calcData.monthlyFee)})
                </span>
              </span>
              <span className="font-medium">{formatCurrency(calcData.planFee)}</span>
            </div>
            {showDiscount && disc > 0 && (
              <div className="flex justify-between px-4 py-2.5 bg-green-50 text-sm border-b border-gray-100 text-green-600">
                <span>Discount</span>
                <span>− {formatCurrency(disc)}</span>
              </div>
            )}
            <div className="flex justify-between px-4 py-3 bg-blue-50">
              <span className="font-bold text-gray-800">Total Payable</span>
              <span className="font-bold text-xl text-blue-600">{formatCurrency(payable)}</span>
            </div>
          </div>

          {/* New expiry preview */}
          {calcData.newExpiry && (
            <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
              <span className="text-green-600 text-lg">📅</span>
              <div>
                <p className="text-xs text-gray-500 font-medium">New Expiry After Payment</p>
                <p className="text-sm font-bold text-green-700">{formatDate(calcData.newExpiry)}</p>
              </div>
            </div>
          )}

          {/* Discount — only for quarterly/yearly */}
          {showDiscount && (
            <div>
              <label className="label">Discount (₹) — optional</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold">₹</span>
                <input className="input pl-7" type="number" min="0" value={discount}
                  onChange={e => setDiscount(e.target.value)} placeholder="0" />
              </div>
            </div>
          )}

          {/* Payment mode */}
          <div>
            <label className="label">Payment Mode</label>
            <div className="flex gap-2">
              {['Cash', 'UPI', 'Online'].map(m => (
                <button key={m} type="button" onClick={() => setMode(m)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 transition-all ${mode === m ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500'}`}>
                  {m === 'Cash' ? '💵' : m === 'UPI' ? '📱' : '🏦'} {m}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button onClick={handlePay} disabled={loading}
              className="btn-success flex-1 flex items-center justify-center gap-2">
              {loading
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
                : `✓ Collect ${formatCurrency(payable)}`}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Members() {
  const [members, setMembers] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterShift, setFilterShift] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: '' });
  const [quickPayMember, setQuickPayMember] = useState(null);
  const navigate = useNavigate();

  const fetchMembers = async () => {
    try {
      const [memRes, priceRes] = await Promise.all([
        membersAPI.getAll({ search, shift: filterShift, status: filterStatus, plan: filterPlan }),
        settingsAPI.getPricing()
      ]);
      setMembers(memRes.data);
      setPricing(priceRes.data);
    } catch {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(fetchMembers, 300);
    return () => clearTimeout(timer);
  }, [search, filterShift, filterStatus, filterPlan]);

  const handleFreeze = async (id, status) => {
    try {
      await membersAPI.freeze(id);
      toast.success(status === 'Frozen' ? 'Membership unfrozen' : 'Membership frozen');
      fetchMembers();
    } catch { toast.error('Action failed'); }
  };

  const handleDelete = async () => {
    try {
      await membersAPI.delete(deleteModal.id);
      toast.success('Member deleted');
      setDeleteModal({ open: false, id: null, name: '' });
      fetchMembers();
    } catch { toast.error('Delete failed'); }
  };

  // Per-member expected plan fee (no admission)
  const getPlanFee = (plan) => {
    if (!pricing) return 0;
    return pricing.monthlyFee * (PLAN_MONTHS[plan] || 1);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Members</h2>
          <p className="text-gray-500 text-sm">{members.length} total members</p>
        </div>
        <button onClick={() => navigate('/members/add')} className="btn-primary">+ Add Member</button>
      </div>

      {/* Filters */}
      <div className="card py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input className="input" placeholder="Search by name..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="input" value={filterShift} onChange={e => setFilterShift(e.target.value)}>
            <option value="">All Shifts</option>
            <option>Morning</option>
            <option>Evening</option>
            <option>Both</option>
          </select>
          <select className="input" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            <option>Active</option>
            <option>Expiring Soon</option>
            <option>Expired</option>
            <option>Frozen</option>
          </select>
          <select className="input" value={filterPlan} onChange={e => setFilterPlan(e.target.value)}>
            <option value="">All Plans</option>
            <option>Monthly</option>
            <option>Quarterly</option>
            <option>Yearly</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? <LoadingSpinner /> : members.length === 0 ? (
        <div className="card text-center py-16">
          <div className="text-5xl mb-4">👤</div>
          <p className="text-gray-500 font-medium">No members found</p>
          <p className="text-gray-400 text-sm mt-1">Try adjusting your filters or add a new member</p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="table-header">Member</th>
                  <th className="table-header">ID</th>
                  <th className="table-header">Plan / Shift</th>
                  <th className="table-header">Expiry</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Plan Fee</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map(member => (
                  <tr
                    key={member._id}
                    className="transition-colors cursor-pointer"
                    style={{ borderBottom: '1px solid rgba(0,255,240,0.05)' }}
                    onMouseEnter={e => e.currentTarget.style.background='rgba(0,255,240,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                    onClick={() => navigate(`/members/${member._id}`)}
                  >
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <MemberAvatar photo={member.photo} name={member.name} size="sm" />
                        <div>
                          <p className="font-medium text-sm" style={{ color: 'var(--text)' }}>{member.name}</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{member.phone}</p>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background:'rgba(0,255,240,0.06)', color:'var(--aqua)' }}>{member.memberId}</span>
                    </td>
                    <td className="table-cell">
                      <p className="text-sm" style={{ color:'var(--text)' }}>{member.plan}</p>
                      <p className="text-xs" style={{ color:'var(--text-muted)' }}>{member.shift}</p>
                    </td>
                    <td className="table-cell">
                      <p className="text-sm" style={{ color:'var(--text)' }}>{formatDate(member.expiryDate)}</p>
                      <DaysRemaining expiryDate={member.expiryDate} status={member.status} />
                    </td>
                    <td className="table-cell">
                      <StatusBadge status={member.status} />
                    </td>
                    <td className="table-cell">
                      <p className="text-sm font-semibold" style={{ color:'var(--text)' }}>{formatCurrency(getPlanFee(member.plan))}</p>
                      <p className="text-xs" style={{ color:'var(--text-muted)' }}>per cycle</p>
                    </td>
                    <td className="table-cell" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setQuickPayMember(member)}
                          title="Mark as Paid"
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors"
                          style={{ background:'rgba(52,211,153,0.12)', color:'#34D399', border:'1px solid rgba(52,211,153,0.2)' }}
                          onMouseEnter={e => e.currentTarget.style.background='rgba(52,211,153,0.22)'}
                          onMouseLeave={e => e.currentTarget.style.background='rgba(52,211,153,0.12)'}
                        >✓ Paid</button>
                        <button onClick={() => navigate(`/members/edit/${member._id}`)} title="Edit"
                          className="p-1.5 rounded-lg transition-colors text-base"
                          style={{ color:'var(--text-muted)' }}
                          onMouseEnter={e => { e.currentTarget.style.color='var(--aqua)'; e.currentTarget.style.background='rgba(0,255,240,0.08)'; }}
                          onMouseLeave={e => { e.currentTarget.style.color='var(--text-muted)'; e.currentTarget.style.background='transparent'; }}
                        >✏️</button>
                        <button onClick={() => handleFreeze(member._id, member.status)} title={member.status === 'Frozen' ? 'Unfreeze' : 'Freeze'}
                          className="p-1.5 rounded-lg transition-colors text-base"
                          style={{ color:'var(--text-muted)' }}
                          onMouseEnter={e => { e.currentTarget.style.color='#FBBF24'; e.currentTarget.style.background='rgba(251,191,36,0.08)'; }}
                          onMouseLeave={e => { e.currentTarget.style.color='var(--text-muted)'; e.currentTarget.style.background='transparent'; }}
                        >{member.status === 'Frozen' ? '🔓' : '🔒'}</button>
                        <button onClick={() => setDeleteModal({ open: true, id: member._id, name: member.name })} title="Delete"
                          className="p-1.5 rounded-lg transition-colors text-base"
                          style={{ color:'var(--text-muted)' }}
                          onMouseEnter={e => { e.currentTarget.style.color='#FF4060'; e.currentTarget.style.background='rgba(255,64,96,0.08)'; }}
                          onMouseLeave={e => { e.currentTarget.style.color='var(--text-muted)'; e.currentTarget.style.background='transparent'; }}
                        >🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick Pay Modal */}
      {quickPayMember && (
        <QuickPayModal
          member={quickPayMember}
          pricing={pricing}
          onClose={() => setQuickPayMember(null)}
          onSuccess={() => { setQuickPayMember(null); fetchMembers(); }}
        />
      )}

      <ConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null, name: '' })}
        onConfirm={handleDelete}
        title="Delete Member"
        message={`Are you sure you want to delete ${deleteModal.name}? This cannot be undone.`}
      />
    </div>
  );
}

export default Members;
